import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import EpsfPageBreadcrumb from "../EpsfPageBreadcrumb";
import DropdownMenu from "../../../common/DropDownMenu/dropdownmenu";
import { EpsfAccessGate, useEpsfAccess } from "../epsfAccess";
import { findOption, useEpsfStudySite } from "../epsfLedgerUi";
import {
  fetchHeldReceipts,
  fetchImpBalances,
  fetchImpKits,
  fetchImpProducts,
  fetchImpTxns,
} from "../epsfService";
import InventoryDetailModal from "./inventory/InventoryDetailModal";
import ReviewHeldStockModal from "./inventory/ReviewHeldStockModal";
import ImpReceipt from "./ImpReceipt";
import ImpDispense from "./ImpDispense";
import ImpReturns from "./ImpReturns";
import ImpDestruction from "./ImpDestruction";
import ImpAccountability from "./ImpAccountability";
import {
  DASHBOARD_IMP_CARD_LIMIT,
  EXPIRING_SOON_DAYS_DEFAULT,
  batchCountLabel,
  buildAttentionItems,
  buildProductMetaMap,
  buildScopeSummary,
  enrichBalanceRows,
  formatExpiryDisplay,
  formatQtyWithUnit,
  groupBalancesByImp,
  pickPreferredBatchId,
  sortImpGroups,
} from "./inventory/inventoryDashboardUtils";
import "../EpsfPages.scss";

const KPI_META = {
  available: { icon: "fas fa-check-circle", tone: "ok", view: "batches" },
  held: { icon: "fas fa-pause-circle", tone: "review", view: "held" },
  expiring: { icon: "fas fa-hourglass-half", tone: "warning", view: "batches" },
  expired: { icon: "fas fa-calendar-times", tone: "danger", view: "batches" },
};

function statusBadges(group) {
  const badges = [];
  if (group.hasRecalled) badges.push({ key: "recall", label: "Recalled", tone: "danger" });
  if (group.hasExpired) badges.push({ key: "expired", label: "Expired", tone: "danger" });
  if (group.hasExpiringSoon) {
    badges.push({ key: "soon", label: "Expiring", tone: "warning" });
  }
  if (group.hasHeld) badges.push({ key: "held", label: "On hold", tone: "review" });
  if (group.available > 0) badges.push({ key: "ok", label: "Available", tone: "ok" });
  if (!badges.length) badges.push({ key: "onhand", label: "On hand", tone: "neutral" });
  return badges;
}

function primaryStatusTone(badges) {
  const order = ["danger", "warning", "review", "ok", "neutral"];
  for (const tone of order) {
    if (badges.some((b) => b.tone === tone)) return tone;
  }
  return "neutral";
}

function buildImpCardActions(group, { access, onViewDetails, onReviewHeld, onAction }) {
  const available = Number(group.available || 0);
  const onHand = Number(group.onHand || 0);
  const held = Number(group.held || 0);
  const hasBatches = Number(group.batchCount || 0) > 0;
  const items = [
    {
      icon: "fas fa-info-circle",
      label: "View details",
      onClick: () => onViewDetails(group),
    },
  ];

  // Receive is always possible when permitted (new stock can arrive for this IMP).
  if (access.canReceive) {
    items.push({
      icon: "fas fa-truck-loading",
      label: "Receive stock",
      onClick: () => onAction("receive", group),
    });
  }

  // Dispense only from Available (held / quarantine stock cannot be dispensed).
  if (access.canDispense && available > 0) {
    items.push({
      icon: "fas fa-hand-holding-medical",
      label: "Dispense",
      onClick: () => onAction("dispense", group),
    });
  }

  // Stock return needs on-hand (including held) to send back to sponsor/depot.
  if (access.canReturn && onHand > 0) {
    items.push({
      icon: "fas fa-undo",
      label: "Returns",
      onClick: () => onAction("returns", group),
    });
  }

  // Destruction needs physical stock on site.
  if (access.canDestroy && onHand > 0) {
    items.push({
      icon: "fas fa-trash-alt",
      label: "Destruction",
      onClick: () => onAction("destruction", group),
    });
  }

  // Reconcile only when there is at least one batch to count.
  if (access.canReconcile && hasBatches) {
    items.push({
      icon: "fas fa-balance-scale",
      label: "Reconcile",
      onClick: () => onAction("reconcile", group),
    });
  }

  // Held review only when something is actually on hold.
  if (held > 0 || group.hasHeld) {
    items.push({
      icon: "fas fa-pause-circle",
      label: "Review held stock",
      onClick: () => onReviewHeld(group),
    });
  }

  return items;
}

function ImpOverviewCard({
  group,
  access,
  onViewDetails,
  onReviewHeld,
  onAction,
}) {
  const badges = statusBadges(group);
  const tone = primaryStatusTone(badges);
  const primaryBadge = badges[0];
  const secondaryBadges = badges.slice(1);
  const [actionsOpen, setActionsOpen] = useState(false);
  const actionsTriggerRef = useRef(null);
  const primaryQty =
    group.available > 0
      ? { label: "Available", value: formatQtyWithUnit(group.available, group) }
      : { label: "On hand", value: formatQtyWithUnit(group.onHand, group) };

  const actionItems = useMemo(
    () =>
      buildImpCardActions(group, {
        access,
        onViewDetails,
        onReviewHeld,
        onAction,
      }),
    [group, access, onViewDetails, onReviewHeld, onAction],
  );

  return (
    <article className={`epsf-inv-dash__imp-card epsf-inv-dash__imp-card--${tone}`}>
      <div className="epsf-inv-dash__imp-card-top">
        <div className="epsf-inv-dash__imp-card-head">
          <span className="epsf-inv-dash__imp-code-badge" title={group.productCode || ""}>
            {group.productCode || "—"}
          </span>
          <h3 className="epsf-inv-dash__imp-name" title={group.productName || ""}>
            {group.productName || "—"}
          </h3>
          <div className="epsf-inv-dash__imp-card-tools">
            {primaryBadge ? (
              <span
                className={`epsf-inv-dash__status-chip epsf-inv-dash__status-chip--${primaryBadge.tone}`}
              >
                {primaryBadge.label}
              </span>
            ) : null}
            <div className="epsf-inv-dash__imp-menu-wrap">
              <button
                ref={actionsTriggerRef}
                type="button"
                className="epsf-inv-dash__imp-menu-btn"
                onClick={() => setActionsOpen((prev) => !prev)}
                aria-expanded={actionsOpen}
                aria-haspopup="true"
                aria-label={`Actions for ${group.productName || "IMP"}`}
              >
                <i className="fas fa-ellipsis-v" aria-hidden="true" />
              </button>
              <DropdownMenu
                items={actionItems.map((action) => ({
                  ...action,
                  onClick: () => {
                    setActionsOpen(false);
                    action.onClick?.();
                  },
                }))}
                show={actionsOpen}
                onClose={() => setActionsOpen(false)}
                trigger={actionsTriggerRef}
                align="end"
                zIndex={2000}
              />
            </div>
          </div>
        </div>
        <div className="epsf-inv-dash__imp-code-row">
          <span
            className={`epsf-inv-dash__tracking-pill epsf-inv-dash__tracking-pill--${
              group.trackingMethod === "IndividualKit" ? "kit" : "batch"
            }`}
          >
            {group.trackingMethod === "IndividualKit" ? "Kit" : "Batch"}
          </span>
        </div>
      </div>

      {secondaryBadges.length > 0 ? (
        <ul className="epsf-inv-dash__cond-list" aria-label="Additional conditions">
          {secondaryBadges.map((b) => (
            <li
              key={b.key}
              className={`epsf-inv-dash__cond epsf-inv-dash__cond--${b.tone}`}
            >
              <span className="epsf-inv-dash__cond-dot" aria-hidden="true" />
              {b.label}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="epsf-inv-dash__metric-panel">
        <div className="epsf-inv-dash__metric-grid epsf-inv-dash__metric-grid--compact">
          <div>
            <span>{primaryQty.label}</span>
            <strong>{primaryQty.value}</strong>
          </div>
          <div>
            <span>Hold</span>
            <strong>
              {group.held > 0 ? formatQtyWithUnit(group.held, group) : "—"}
            </strong>
          </div>
          <div>
            <span>Batches</span>
            <strong>{group.batchCount}</strong>
          </div>
          <div>
            <span>Nearest expiry</span>
            <strong>{formatExpiryDisplay(group.nearestExpiry)}</strong>
          </div>
        </div>
      </div>

      {group.storageSummary && group.storageSummary !== "—" ? (
        <p className="epsf-inv-dash__imp-meta mb-0" title={group.storageSummary}>
          <i className="fas fa-warehouse" aria-hidden="true" />
          <span>{group.storageSummary}</span>
        </p>
      ) : null}
    </article>
  );
}

export default function ImpInventory() {
  const navigate = useNavigate();
  const { loading, canView, moduleAccess, canReceive, canDispense, canReturn, canDestroy, canReconcile } =
    useEpsfAccess();
  const allow = canView && moduleAccess;
  const cardAccess = useMemo(
    () => ({ canReceive, canDispense, canReturn, canDestroy, canReconcile }),
    [canReceive, canDispense, canReturn, canDestroy, canReconcile],
  );
  const ctx = useEpsfStudySite();

  const [balances, setBalances] = useState([]);
  const [held, setHeld] = useState([]);
  const [kits, setKits] = useState([]);
  const [movements, setMovements] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [kitsBusy, setKitsBusy] = useState(false);
  const [movementsBusy, setMovementsBusy] = useState(false);
  const [releaseBusyId, setReleaseBusyId] = useState("");
  const [reviewRow, setReviewRow] = useState(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [expiringDays] = useState(EXPIRING_SOON_DAYS_DEFAULT);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalFilter, setModalFilter] = useState("all");
  const [modalImpId, setModalImpId] = useState(null);
  const [modalView, setModalView] = useState("batches");
  const [actionOverlay, setActionOverlay] = useState(null);

  const requestIdRef = useRef(0);
  const dashScrollRef = useRef(null);
  const scrollPosRef = useRef(0);

  const contextReady = Boolean(ctx.studyId && ctx.siteId);
  const studyLabel =
    findOption(ctx.studyOptions, ctx.studyId)?.label || ctx.studyId || "";
  const siteLabel =
    findOption(ctx.siteOptions, ctx.siteId)?.label || ctx.siteId || "";

  const clearDashboardData = useCallback(() => {
    setBalances([]);
    setHeld([]);
    setKits([]);
    setMovements([]);
    setLastRefreshedAt(null);
  }, []);

  const refresh = useCallback(async () => {
    const reqId = ++requestIdRef.current;
    if (!ctx.studyId || !ctx.siteId) {
      clearDashboardData();
      setError("");
      setBusy(false);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const [balRes, heldRes, prodRes] = await Promise.all([
        fetchImpBalances(ctx.studyId, ctx.siteId),
        fetchHeldReceipts(ctx.studyId, ctx.siteId),
        fetchImpProducts(null, null, null),
      ]);
      if (reqId !== requestIdRef.current) return;

      if (!balRes.success) {
        clearDashboardData();
        setError(balRes.message || "Could not load inventory balances.");
        return;
      }
      const meta = buildProductMetaMap(prodRes.success ? prodRes.rows : []);
      setBalances(enrichBalanceRows(balRes.rows || [], meta));
      if (heldRes.success) setHeld(heldRes.rows || []);
      else setHeld([]);
      if (!heldRes.success && heldRes.message) setError(heldRes.message);
      setLastRefreshedAt(new Date());
    } catch (e) {
      if (reqId !== requestIdRef.current) return;
      clearDashboardData();
      setError(e?.message || "Could not load inventory.");
    } finally {
      if (reqId === requestIdRef.current) setBusy(false);
    }
  }, [ctx.studyId, ctx.siteId, clearDashboardData]);

  useEffect(() => {
    refresh();
    setModalOpen(false);
    setModalFilter("all");
    setModalImpId(null);
  }, [refresh]);

  const loadKits = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setKits([]);
      return;
    }
    setKitsBusy(true);
    const reqStudy = ctx.studyId;
    const reqSite = ctx.siteId;
    try {
      const result = await fetchImpKits(reqStudy, reqSite, {
        impProductId: modalImpId || null,
      });
      if (reqStudy !== ctx.studyId || reqSite !== ctx.siteId) return;
      if (result.success) setKits(result.rows || []);
      else if (result.message) setError(result.message);
    } finally {
      setKitsBusy(false);
    }
  }, [ctx.studyId, ctx.siteId, modalImpId]);

  const loadMovements = useCallback(async () => {
    if (!ctx.studyId || !ctx.siteId) {
      setMovements([]);
      return;
    }
    setMovementsBusy(true);
    const reqStudy = ctx.studyId;
    const reqSite = ctx.siteId;
    try {
      const result = await fetchImpTxns(reqStudy, reqSite, null);
      if (reqStudy !== ctx.studyId || reqSite !== ctx.siteId) return;
      if (result.success) setMovements(result.rows || []);
      else if (result.message) setError(result.message);
    } finally {
      setMovementsBusy(false);
    }
  }, [ctx.studyId, ctx.siteId]);

  useEffect(() => {
    if (!modalOpen) return;
    if (modalView === "kits") loadKits();
    if (modalView === "movements") loadMovements();
  }, [modalOpen, modalView, loadKits, loadMovements]);

  const summary = useMemo(
    () => buildScopeSummary(balances, held, expiringDays),
    [balances, held, expiringDays],
  );
  const attentionItems = useMemo(
    () => buildAttentionItems(summary, balances, held, expiringDays),
    [summary, balances, held, expiringDays],
  );
  const impGroups = useMemo(
    () => sortImpGroups(groupBalancesByImp(balances, expiringDays), "name"),
    [balances, expiringDays],
  );
  const overviewGroups = useMemo(
    () => impGroups.slice(0, DASHBOARD_IMP_CARD_LIMIT),
    [impGroups],
  );
  const hasMoreImps = impGroups.length > overviewGroups.length;

  const modalImpGroup = useMemo(
    () =>
      impGroups.find(
        (g) =>
          String(g.impProductId || "").toLowerCase() ===
          String(modalImpId || "").toLowerCase(),
      ) || null,
    [impGroups, modalImpId],
  );

  const openModal = ({ filter = "all", impId = null, view = "batches" } = {}) => {
    const scrollEl = dashScrollRef.current?.querySelector?.(
      ".epsf-inv-dash__scroll",
    );
    scrollPosRef.current = scrollEl?.scrollTop || 0;
    setModalFilter(filter);
    setModalImpId(impId);
    setModalView(view);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    window.requestAnimationFrame(() => {
      const scrollEl = dashScrollRef.current?.querySelector?.(
        ".epsf-inv-dash__scroll",
      );
      if (scrollEl) scrollEl.scrollTop = scrollPosRef.current || 0;
    });
  };

  const openViewDetails = (group) => {
    openModal({
      filter: "all",
      impId: group.impProductId,
      view: "batches",
    });
  };

  const openReviewHeld = (group) => {
    const productId = String(group?.impProductId || "").toLowerCase();
    if (!productId) {
      setError("Could not open review for this IMP.");
      return;
    }

    const matches = (held || []).filter((h) => {
      const id = String(h.ImpProductId || h.impProductId || "").toLowerCase();
      return id && id === productId;
    });

    if (!matches.length) {
      setError(
        "No held receipt was found for this IMP. Refresh inventory and try again.",
      );
      return;
    }

    const heldRemaining = (row) => {
      const explicit = row.HeldQuantity ?? row.heldQuantity;
      if (explicit != null && explicit !== "") {
        const n = Number(explicit);
        return Number.isFinite(n) ? n : 0;
      }
      const total = Number(row.Quantity ?? row.quantity ?? 0);
      const released = Number(row.ReleasedQuantity ?? row.releasedQuantity ?? 0);
      const ext = Number(
        row.ExternallyReturnedQuantity ?? row.externallyReturnedQuantity ?? 0,
      );
      return Math.max(0, total - released - ext);
    };

    // Prefer the receipt with the most remaining held qty (then latest).
    const sorted = [...matches].sort((a, b) => {
      const diff = heldRemaining(b) - heldRemaining(a);
      if (diff !== 0) return diff;
      const ta = new Date(a.TxnAt || a.txnAt || 0).getTime();
      const tb = new Date(b.TxnAt || b.txnAt || 0).getTime();
      return tb - ta;
    });

    setError("");
    setReviewRow(sorted[0]);
  };

  const openActionOverlay = (type, group) => {
    const mode =
      type === "dispense"
        ? "available"
        : type === "returns"
          ? "heldFirst"
          : "onHand";
    setActionOverlay({
      type,
      group,
      preferImpBatchId: pickPreferredBatchId(group?.batches, mode),
    });
  };

  const closeActionOverlay = () => setActionOverlay(null);

  const onActionOverlaySuccess = () => {
    setActionOverlay(null);
    refresh();
  };

  const overlayProductId = actionOverlay?.group?.impProductId || null;
  const overlayProps = {
    overlay: true,
    preferImpProductId: overlayProductId,
    preferImpBatchId: actionOverlay?.preferImpBatchId || null,
    preferSourceTxnId: actionOverlay?.preferSourceTxnId || null,
    onOverlayClose: closeActionOverlay,
    onOverlaySuccess: onActionOverlaySuccess,
  };

  const onRelease = (row) => {
    const txnId = row?.ImpTxnId || row?.impTxnId;
    if (!txnId || releaseBusyId) return;
    setReviewRow(row);
  };

  const closeReviewModal = () => {
    if (releaseBusyId) return;
    setReviewRow(null);
  };

  const onReviewConfirmed = async ({ disposition, row }) => {
    setReviewRow(null);
    setError("");
    await refresh();
    if (modalView === "kits") await loadKits();
    if (modalView === "movements") await loadMovements();

    const batchId = String(row?.ImpBatchId || row?.impBatchId || "");
    const productId = String(row?.ImpProductId || row?.impProductId || "");
    const sourceTxnId = String(row?.ImpTxnId || row?.impTxnId || "");

    if (disposition === "ReturnPending" && canReturn) {
      setActionOverlay({
        type: "returns",
        group: { impProductId: productId },
        preferImpBatchId: batchId || null,
        preferSourceTxnId: sourceTxnId || null,
      });
    } else if (disposition === "DestructionPending" && canDestroy) {
      setActionOverlay({
        type: "destruction",
        group: { impProductId: productId },
        preferImpBatchId: batchId || null,
        preferSourceTxnId: sourceTxnId || null,
      });
    }
  };

  const contextChip = contextReady
    ? `${studyLabel} · ${siteLabel}`
    : "Select study and site";
  const lastRefreshedLabel = lastRefreshedAt
    ? lastRefreshedAt.toLocaleString()
    : null;

  const kpiItems = [
    {
      key: "available",
      label: "Available",
      value: summary.availableQuantity,
      measure: summary.availableAllKits
        ? `kits · ${batchCountLabel(summary.availableBatchCount)}`
        : batchCountLabel(summary.availableBatchCount),
    },
    {
      key: "held",
      label: "Hold / review",
      value: summary.heldQuantity,
      measure: summary.heldAllKits
        ? `kits · ${batchCountLabel(summary.heldBatchCount)}`
        : batchCountLabel(summary.heldBatchCount),
    },
    {
      key: "expiring",
      label: "Expiring soon",
      value: summary.expiringBatchCount,
      measure:
        summary.expiringBatchCount === 1
          ? `batch within ${expiringDays} days`
          : `batches within ${expiringDays} days`,
    },
    {
      key: "expired",
      label: "Expired",
      value: summary.expiredBatchCount,
      measure: batchCountLabel(summary.expiredBatchCount),
    },
  ];

  return (
    <EpsfAccessGate allow={allow} loading={loading}>
      <div className="epsf-list-page epsf-inv-dash" ref={dashScrollRef}>
        <EpsfPageBreadcrumb
          items={[
            { label: "ePSF", path: "/epsf/imp/inventory" },
            { label: "IMP" },
            { label: "Inventory" },
          ]}
          actions={[
            {
              icon: "fas fa-refresh",
              label: busy ? "Refreshing…" : "Refresh",
              onClick: refresh,
            },
            {
              icon: "fas fa-table",
              label: "View all inventory",
              onClick: () => {
                if (!ctx.studyId || !ctx.siteId) return;
                openModal({ filter: "all", view: "batches" });
              },
            },
          ]}
          trailingExtras={
            lastRefreshedLabel ? (
              <span className="epsf-inv-dash__refreshed">
                Updated {lastRefreshedLabel}
              </span>
            ) : null
          }
        />

        {error ? (
          <div className="epsf-inv-dash__error" role="alert">
            <span>{error}</span>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={refresh}
            >
              Retry
            </button>
          </div>
        ) : null}

        {!contextReady ? (
          <div className="epsf-inv-dash__empty">
            <p className="mb-0">
              Select study and site in the breadcrumb bar to view inventory.
            </p>
          </div>
        ) : null}

        {contextReady && busy && balances.length === 0 && !error ? (
          <div className="epsf-inv-dash__skeleton" aria-busy="true">
            <div className="epsf-inv-dash__skeleton-kpi" />
            <div className="epsf-inv-dash__skeleton-grid">
              <div />
              <div />
              <div />
            </div>
          </div>
        ) : null}

        {contextReady && !error ? (
          <>
            <div className="epsf-inv-dash__chrome">
              <div
                className="epsf-inv-dash__kpi"
                role="toolbar"
                aria-label="Inventory metrics"
              >
                {kpiItems.map((item) => {
                  const meta = KPI_META[item.key] || {};
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`epsf-inv-dash__kpi-item epsf-inv-dash__kpi-item--${meta.tone}`}
                      onClick={() =>
                        openModal({
                          filter: item.key,
                          view: meta.view || "batches",
                        })
                      }
                      aria-label={`${item.label}: ${item.value} ${item.measure}. Open details.`}
                    >
                      <span className="epsf-inv-dash__kpi-icon" aria-hidden>
                        <i className={meta.icon} />
                      </span>
                      <span className="epsf-inv-dash__kpi-copy">
                        <span className="epsf-inv-dash__kpi-value">
                          {item.value}
                        </span>
                        <span className="epsf-inv-dash__kpi-label">
                          {item.label}
                        </span>
                        <span className="epsf-inv-dash__kpi-sub">
                          {item.measure}
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>

              <section
                className="epsf-inv-dash__attention"
                aria-label="Needs attention"
              >
                {attentionItems.length === 0 ? (
                  <div className="epsf-inv-dash__attention-ok">
                    <i className="fas fa-shield-alt" aria-hidden />
                    <span>
                      No expired, expiring, held, or recalled stock in this
                      scope.
                    </span>
                  </div>
                ) : (
                  <div className="epsf-inv-dash__attention-grid">
                    {attentionItems.map((item) => (
                      <button
                        key={item.id}
                        type="button"
                        className={`epsf-inv-dash__attention-row epsf-inv-dash__attention-row--${item.tone}`}
                        onClick={() =>
                          openModal({
                            filter: item.filter,
                            view: item.view || "batches",
                          })
                        }
                      >
                        <i className={item.icon} aria-hidden />
                        <span className="epsf-inv-dash__attention-text">
                          <strong>{item.label}</strong>
                          <span>
                            {item.count} {item.countLabel}
                            {item.refs?.length
                              ? ` — ${item.refs.join("; ")}`
                              : ""}
                            {item.count > (item.refs?.length || 0)
                              ? "…"
                              : ""}
                          </span>
                        </span>
                        <span className="epsf-inv-dash__attention-action">
                          {item.actionLabel || "Open"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </section>

              <div className="epsf-inv-dash__section-head">
                <h3>IMP overview</h3>
                <button
                  type="button"
                  className="btn btn-link btn-sm px-0"
                  onClick={() =>
                    openModal({ filter: "all", view: "batches" })
                  }
                >
                  View all inventory
                </button>
              </div>
            </div>

            <div className="epsf-inv-dash__scroll">
              {!busy && balances.length === 0 ? (
                <div className="epsf-inv-dash__empty">
                  <p className="mb-2">
                    No stock has been received for this study/site yet.
                  </p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate("/epsf/imp/receipt")}
                  >
                    Receive IMP
                  </button>
                </div>
              ) : null}

              <div className="epsf-inv-dash__imp-grid">
                {overviewGroups.map((g) => (
                  <ImpOverviewCard
                    key={String(g.impProductId)}
                    group={g}
                    access={cardAccess}
                    onViewDetails={openViewDetails}
                    onReviewHeld={openReviewHeld}
                    onAction={openActionOverlay}
                  />
                ))}
              </div>

              {hasMoreImps ? (
                <div className="epsf-inv-dash__more">
                  <span>
                    Showing {overviewGroups.length} of {impGroups.length} IMPs
                  </span>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() =>
                      openModal({ filter: "all", view: "batches" })
                    }
                  >
                    View all inventory
                  </button>
                </div>
              ) : null}
            </div>
          </>
        ) : null}

        <InventoryDetailModal
          open={modalOpen}
          onClose={closeModal}
          title={
            modalImpGroup
              ? `${modalImpGroup.productName || "IMP"} details`
              : "Inventory details"
          }
          contextLabel={contextReady ? contextChip : ""}
          filterKey={modalFilter}
          expiringDays={expiringDays}
          impProductId={modalImpId}
          impGroup={modalImpGroup}
          balances={balances}
          held={held}
          kits={kits}
          kitsBusy={kitsBusy}
          movements={movements}
          movementsBusy={movementsBusy}
          view={modalView}
          onViewChange={setModalView}
          canReceive={canReceive}
          releaseBusyId={releaseBusyId}
          onRelease={onRelease}
          onClearFilter={() => setModalFilter("all")}
          onClearImp={() => setModalImpId(null)}
          onFilterKeyChange={setModalFilter}
        />

        <ReviewHeldStockModal
          open={Boolean(reviewRow)}
          row={reviewRow}
          studyId={ctx.studyId}
          siteId={ctx.siteId}
          busy={Boolean(releaseBusyId)}
          onClose={closeReviewModal}
          onConfirm={onReviewConfirmed}
        />

        {actionOverlay?.type === "receive" ? (
          <ImpReceipt key={`ov-receive-${overlayProductId}`} {...overlayProps} />
        ) : null}
        {actionOverlay?.type === "dispense" ? (
          <ImpDispense key={`ov-dispense-${overlayProductId}`} {...overlayProps} />
        ) : null}
        {actionOverlay?.type === "returns" ? (
          <ImpReturns key={`ov-returns-${overlayProductId}`} {...overlayProps} />
        ) : null}
        {actionOverlay?.type === "destruction" ? (
          <ImpDestruction
            key={`ov-destroy-${overlayProductId}`}
            {...overlayProps}
          />
        ) : null}
        {actionOverlay?.type === "reconcile" ? (
          <ImpAccountability
            key={`ov-reconcile-${overlayProductId}`}
            {...overlayProps}
          />
        ) : null}
      </div>
    </EpsfAccessGate>
  );
}
